import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import type { ApiError, ApiResponse, User, Machine, BodyweightExercise, Workout, ExerciseSet, GymFloorPlan, PaginatedResponse, BodyweightSubcategory, SystemNotification } from '../types';

// Base API configuration
const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

// Create axios instance
const api: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000,
});

// Token storage key
const TOKEN_KEY = 'paingain_token';

// Get token from localStorage
export const getToken = (): string | null => {
  return localStorage.getItem(TOKEN_KEY);
};

// Set token in localStorage
export const setToken = (token: string): void => {
  localStorage.setItem(TOKEN_KEY, token);
};

// Remove token from localStorage
export const removeToken = (): void => {
  localStorage.removeItem(TOKEN_KEY);
};

// Request interceptor - add auth token
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = getToken();
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - handle errors
api.interceptors.response.use(
  (response) => response,
  (error: AxiosError<{ error: ApiError }>) => {
    // Only trigger logout if we have a token (user was logged in)
    // Don't trigger on login/register failures
    if (error.response?.status === 401 && getToken()) {
      removeToken();
      window.dispatchEvent(new CustomEvent('auth:logout'));
    }

    const apiError: ApiError = error.response?.data?.error || {
      code: 'NETWORK_ERROR',
      message: error.message || 'Network error occurred',
    };

    return Promise.reject(apiError);
  }
);

// ============================================
// AUTH API
// ============================================

export const authApi = {
  login: async (email: string, password: string) => {
    const response = await api.post<ApiResponse<{ user: User; token: string }>>('/auth/login', { email, password });
    return response.data;
  },

  register: async (email: string, password: string, name: string) => {
    const response = await api.post<ApiResponse<{ user: User; token: string }>>('/auth/register', { email, password, name });
    return response.data;
  },

  logout: async () => {
    await api.post('/auth/logout');
    removeToken();
  },

  me: async () => {
    const response = await api.get<ApiResponse<{ user: User }>>('/auth/me');
    return response.data;
  },

  heartbeat: async () => {
    const response = await api.post<ApiResponse<{ ok: boolean }>>('/auth/heartbeat');
    return response.data;
  },
};

// ============================================
// USER API
// ============================================

export const userApi = {
  getById: async (id: string) => {
    const response = await api.get<ApiResponse<{ user: User & { workoutCount: number } }>>(`/users/${id}`);
    return response.data;
  },

  update: async (id: string, data: Partial<Pick<User, 'name' | 'birthDate' | 'weight' | 'height' | 'darkMode'>>) => {
    const response = await api.put<ApiResponse<{ user: User }>>(`/users/${id}`, data);
    return response.data;
  },

  getStats: async (id: string) => {
    const response = await api.get<ApiResponse<{
      totalWorkouts: number;
      totalExercises: number;
      totalSets: number;
      totalReps: number;
      totalWeightLifted: number;
      averageDifficulty: number;
    }>>(`/users/${id}/stats`);
    return response.data;
  },
};

// ============================================
// MACHINE API
// ============================================

export type MachineSortBy = 'usage' | 'name' | 'createdAt';

export const machineApi = {
  getAll: async (params?: {
    type?: string;
    search?: string;
    sortBy?: MachineSortBy;
    floorPlanId?: string;
    /** Minden gép (inaktív alaprajzhoz tartozók is) – alaprajz szerkesztéshez */
    includeAll?: boolean;
  }) => {
    const queryParams: Record<string, string> = {};
    if (params?.type) queryParams.type = params.type;
    if (params?.search) queryParams.search = params.search;
    if (params?.sortBy) queryParams.sortBy = params.sortBy;
    if (params?.floorPlanId) queryParams.floorPlanId = params.floorPlanId;
    if (params?.includeAll) queryParams.includeAll = 'true';

    const response = await api.get<ApiResponse<{ machines: Machine[] }>>('/machines', {
      params: queryParams,
    });
    return response.data;
  },

  getById: async (id: string) => {
    const response = await api.get<ApiResponse<{ machine: Machine & { exerciseCount: number } }>>(`/machines/${id}`);
    return response.data;
  },

  getTypes: async () => {
    const response = await api.get<ApiResponse<{ types: string[] }>>('/machines/types');
    return response.data;
  },

  create: async (data: Omit<Machine, 'id' | 'createdAt' | 'photoUrl' | 'thumbnailUrl' | 'description' | 'exerciseDescription'>) => {
    const response = await api.post<ApiResponse<{ machine: Machine }>>('/machines', data);
    return response.data;
  },

  update: async (id: string, data: Partial<Omit<Machine, 'id' | 'createdAt'>>) => {
    const response = await api.put<ApiResponse<{ machine: Machine }>>(`/machines/${id}`, data);
    return response.data;
  },

  delete: async (id: string) => {
    const response = await api.delete<ApiResponse<{ deleted: boolean }>>(`/machines/${id}`);
    return response.data;
  },

  getPhotoUploadUrl: async (id: string, filename: string, contentType: string) => {
    const response = await api.post<ApiResponse<{ uploadUrl: string; photoUrl: string }>>(
      `/machines/${id}/photo/upload-url`,
      { filename, contentType }
    );
    return response.data;
  },

  uploadPhoto: async (id: string, file: File) => {
    const formData = new FormData();
    formData.append('photo', file);
    
    const response = await api.post<ApiResponse<{ machine: Machine; photoUrl: string }>>(
      `/machines/${id}/photo`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data;
  },

  getStats: async (id: string, limit = 5) => {
    const response = await api.get<ApiResponse<{
      machineId: string;
      machineName: string;
      totalWorkouts: number;
      overallMaxWeight: number;
      history: Array<{
        date: string;
        sets: number;
        maxWeight: number;
        totalReps: number;
        totalVolume: number;
        avgDifficulty: number;
        details: Array<{
          setNumber: number;
          reps: number;
          weight: number;
          difficulty: number;
        }>;
      }>;
    }>>(`/machines/${id}/stats`, { params: { limit } });
    return response.data;
  },
};

// ============================================
// BODYWEIGHT EXERCISE API
// ============================================

export const bodyweightApi = {
  getAll: async (params?: { subcategory?: string; search?: string }) => {
    const response = await api.get<ApiResponse<{ exercises: BodyweightExercise[] }>>('/bodyweight', { params });
    return response.data;
  },

  getById: async (id: string) => {
    const response = await api.get<ApiResponse<{ exercise: BodyweightExercise & { usageCount: number } }>>(`/bodyweight/${id}`);
    return response.data;
  },

  getMuscleGroups: async () => {
    const response = await api.get<ApiResponse<{ muscleGroups: string[] }>>('/bodyweight/muscle-groups');
    return response.data;
  },

  create: async (data: { name: string; muscleGroup: string; subcategory: BodyweightSubcategory }) => {
    const response = await api.post<ApiResponse<{ exercise: BodyweightExercise }>>('/bodyweight', data);
    return response.data;
  },

  update: async (id: string, data: Partial<{ name: string; muscleGroup: string; subcategory: BodyweightSubcategory; photoUrl: string | null }>) => {
    const response = await api.put<ApiResponse<{ exercise: BodyweightExercise }>>(`/bodyweight/${id}`, data);
    return response.data;
  },

  delete: async (id: string) => {
    const response = await api.delete<ApiResponse<{ deleted: boolean }>>(`/bodyweight/${id}`);
    return response.data;
  },

  uploadPhoto: async (id: string, file: File) => {
    const formData = new FormData();
    formData.append('photo', file);
    
    const response = await api.post<ApiResponse<{ exercise: BodyweightExercise; photoUrl: string }>>(
      `/bodyweight/${id}/photo`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data;
  },

  getStats: async (id: string, limit = 5) => {
    const response = await api.get<ApiResponse<{
      exerciseId: string;
      exerciseName: string;
      totalWorkouts: number;
      history: Array<{
        date: string;
        sets: number;
        totalReps: number;
        avgDifficulty: number;
        details: Array<{
          setNumber: number;
          reps: number;
          weight: number;
          difficulty: number;
        }>;
      }>;
    }>>(`/bodyweight/${id}/stats`, { params: { limit } });
    return response.data;
  },
};

// ============================================
// WORKOUT API
// ============================================

export const workoutApi = {
  getAll: async (cursor?: string) => {
    const params = cursor ? { cursor, limit: 20 } : { limit: 20 };
    const response = await api.get<ApiResponse<PaginatedResponse<Workout>>>('/workouts', { params });
    return response.data;
  },

  getById: async (id: string) => {
    const response = await api.get<ApiResponse<{ workout: Workout }>>(`/workouts/${id}`);
    return response.data;
  },

  getActive: async () => {
    const response = await api.get<ApiResponse<{ workout: Workout | null }>>('/workouts/active');
    return response.data;
  },

  start: async (startMood?: number, startTime?: string) => {
    const response = await api.post<ApiResponse<{ workout: Workout }>>('/workouts', { startMood, startTime });
    return response.data;
  },

  end: async (id: string, data?: { bodyWeight?: number; endMood?: number; notes?: string; endTime?: string }) => {
    const response = await api.put<ApiResponse<{ workout: Workout }>>(`/workouts/${id}/end`, data);
    return response.data;
  },

  update: async (id: string, data: { notes?: string | null; startTime?: string; endTime?: string }) => {
    const response = await api.put<ApiResponse<{ workout: Workout }>>(`/workouts/${id}`, data);
    return response.data;
  },

  delete: async (id: string) => {
    const response = await api.delete<ApiResponse<{ deleted: boolean }>>(`/workouts/${id}`);
    return response.data;
  },

  addExercise: async (workoutId: string, data: { machineId?: string; bodyweightExerciseId?: string }) => {
    const response = await api.post<ApiResponse<{ exercise: Workout['exercises'][0] }>>(
      `/workouts/${workoutId}/exercises`,
      data
    );
    return response.data;
  },
};

// ============================================
// EXERCISE API
// ============================================

// Set data types
type StandardSetData = { setType: 'standard'; reps: number; weight: number; difficulty: number };
type CardioSetData = { setType: 'cardio'; durationSeconds: number; resistanceLevel?: number; difficulty: number };
type DualHandSetData = { setType: 'dualHand'; leftReps: number; rightReps: number; leftWeight: number; rightWeight: number; difficulty: number };
type AddSetData = StandardSetData | CardioSetData | DualHandSetData;

export const exerciseApi = {
  updateExercise: async (exerciseId: string, data: { machineId?: string; bodyweightExerciseId?: string }) => {
    const response = await api.put<ApiResponse<{ exercise: Workout['exercises'][0] }>>(
      `/exercises/${exerciseId}`,
      data
    );
    return response.data;
  },

  addSet: async (exerciseId: string, data: AddSetData) => {
    const response = await api.post<ApiResponse<{ set: ExerciseSet }>>(
      `/exercises/${exerciseId}/sets`,
      data
    );
    return response.data;
  },

  updateNotes: async (exerciseId: string, notes: string | null) => {
    const response = await api.put<ApiResponse<{ exercise: { id: string; notes: string | null } }>>(
      `/exercises/${exerciseId}/notes`,
      { notes }
    );
    return response.data;
  },

  updateTime: async (exerciseId: string, elapsedSeconds: number) => {
    const response = await api.put<ApiResponse<{ exercise: { id: string; elapsedSeconds: number } }>>(
      `/workouts/exercises/${exerciseId}/time`,
      { elapsedSeconds }
    );
    return response.data;
  },

  updateSet: async (setId: string, data: Partial<{
    reps: number;
    weight: number;
    difficulty: number;
    durationSeconds: number;
    resistanceLevel: number;
    leftReps: number;
    rightReps: number;
    leftWeight: number;
    rightWeight: number;
  }>) => {
    const response = await api.put<ApiResponse<{ set: ExerciseSet }>>(
      `/exercises/sets/${setId}`,
      data
    );
    return response.data;
  },

  deleteSet: async (setId: string) => {
    const response = await api.delete<ApiResponse<{ deleted: boolean }>>(
      `/exercises/sets/${setId}`
    );
    return response.data;
  },

  deleteExercise: async (exerciseId: string) => {
    const response = await api.delete<ApiResponse<{ deleted: boolean }>>(
      `/exercises/${exerciseId}`
    );
    return response.data;
  },
};

// ============================================
// FLOOR PLAN API
// ============================================

// ============================================
// COMMUNITY API
// ============================================

export interface CommunityMember {
  id: string;
  name: string;
  birthDate: string | null;
  lastActiveAt: string | null;
  createdAt: string;
  workoutCount: number;
  lastWorkoutAt: string | null;
  lastWorkoutEndTime: string | null;
}

export const communityApi = {
  getMembers: async () => {
    const response = await api.get<ApiResponse<{ members: CommunityMember[] }>>('/community/members');
    return response.data;
  },

  getMemberWorkouts: async (userId: string, cursor?: string) => {
    const params = cursor ? { cursor, limit: 20 } : { limit: 20 };
    const response = await api.get<ApiResponse<PaginatedResponse<Workout>>>(`/community/members/${userId}/workouts`, { params });
    return response.data;
  },

  getWorkoutDetails: async (workoutId: string) => {
    const response = await api.get<ApiResponse<{ workout: Workout & { user: { id: string; name: string } } }>>(`/community/workouts/${workoutId}`);
    return response.data;
  },
};

// ============================================
// FLOOR PLAN API
// ============================================

export const floorPlanApi = {
  getAll: async (params?: { activeOnly?: boolean }) => {
    const query = params?.activeOnly ? '?activeOnly=true' : '';
    const response = await api.get<ApiResponse<{ floorPlans: GymFloorPlan[] }>>(`/floorplans${query}`);
    return response.data;
  },

  getById: async (id: string) => {
    const response = await api.get<ApiResponse<{ floorPlan: GymFloorPlan }>>(`/floorplans/${id}`);
    return response.data;
  },

  create: async (data: { name: string; drawingData: unknown }) => {
    const response = await api.post<ApiResponse<{ floorPlan: GymFloorPlan }>>('/floorplans', data);
    return response.data;
  },

  update: async (id: string, data: { name?: string; drawingData?: unknown; isActive?: boolean }) => {
    const response = await api.put<ApiResponse<{ floorPlan: GymFloorPlan }>>(`/floorplans/${id}`, data);
    return response.data;
  },

  delete: async (id: string) => {
    const response = await api.delete<ApiResponse<{ deleted: boolean }>>(`/floorplans/${id}`);
    return response.data;
  },
};

// ============================================
// AI API
// ============================================

export const aiApi = {
  getWorkoutContext: async (targetDate?: string) => {
    const params = targetDate ? `?targetDate=${targetDate}` : '';
    const response = await api.get<ApiResponse<any>>(`/ai/workout-context${params}`);
    return response.data;
  },

  generateWorkout: async (data: {
    targetDate: string;
    goal: string;
    focusMuscleGroups?: string[];
    muscleGroupConfig?: { name: string; exerciseCount: number; order: number }[];
  }) => {
    const response = await api.post<ApiResponse<any>>('/ai/generate-workout', data);
    return response.data;
  },

  getWorkoutPlans: async () => {
    const response = await api.get<ApiResponse<{ plans: any[] }>>('/ai/workout-plans');
    return response.data;
  },

  getWorkoutPlan: async (id: string) => {
    const response = await api.get<ApiResponse<{ plan: any }>>(`/ai/workout-plans/${id}`);
    return response.data;
  },

  generateTrainingPlan: async (data: {
    name: string;
    goal: string;
    durationDays?: number;
    durationWeeks?: number;
    planType: string;
  }) => {
    const response = await api.post<ApiResponse<any>>('/ai/generate-training-plan', data);
    return response.data;
  },

  getTrainingPlans: async () => {
    const response = await api.get<ApiResponse<{ plans: any[] }>>('/ai/training-plans');
    return response.data;
  },

  getTrainingPlan: async (id: string) => {
    const response = await api.get<ApiResponse<{ plan: any }>>(`/ai/training-plans/${id}`);
    return response.data;
  },

  getMyUsage: async () => {
    const response = await api.get<ApiResponse<any>>('/ai/my-usage');
    return response.data;
  },

  getMyCreditHistory: async () => {
    const response = await api.get<ApiResponse<{ logs: CreditLogEntry[] }>>('/ai/my-credit-history');
    return response.data;
  },

  getWorkoutSettings: async () => {
    const response = await api.get<ApiResponse<{ settings: AiWorkoutSettingsData }>>('/ai/workout-settings');
    return response.data;
  },

  updateWorkoutSettings: async (data: Partial<AiWorkoutSettingsData>) => {
    const response = await api.put<ApiResponse<{ settings: AiWorkoutSettingsData }>>('/ai/workout-settings', data);
    return response.data;
  },
};

// ============================================
// NOTIFICATION API
// ============================================

export const notificationApi = {
  getUnread: async () => {
    const response = await api.get<ApiResponse<{ notifications: SystemNotification[] }>>('/notifications');
    return response.data;
  },

  markAsRead: async (id: string) => {
    const response = await api.post<ApiResponse<{ read: boolean }>>(`/notifications/${id}/read`);
    return response.data;
  },
};

export interface CreditLogEntry {
  id: string;
  source: string;
  model: string;
  totalTokens: number;
  costUsd: number;
  createdAt: string;
}

export interface AiWorkoutSettingsData {
  id: string;
  intensity: string;
  maxWeightIncreasePercent: number;
  difficultyAwareness: boolean;
  restTimePreference: string;
  trainingExperience: string;
}

export default api;
